// import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import { useSelector } from "react-redux";
// import axiosInstance from "../utils/Api";
// import { selectStorageLocation } from "../store/authSlice";
// import { formatQuantity, formatUnit, getErrorMessage } from "../utils/format";

// // ---- APIs used on this screen ----------------------------------------
// // GET stock-indent/stock-indents
// //   ?page=1&limit=500
// //   -> { success, data: [{ _id, indentNumber, status, createdAt,
// //        storageLocation: {...}, outlet: {_id,name,city,state,address,
// //        coordinates:{latitude,longitude}, location:{coordinates:[lng,lat]}},
// //        items: [{ _id, rawMaterial: {...}, requestedQuantity,
// //                  dispatchedQuantity, totalReceivedQuantity, ... }] }],
// //        pagination: { page, limit, totalRecords, totalPages, hasNextPage } }
// //   One big page is fetched so the outlet filter + search work instantly on
// //   the client; "Load more" appears only if the server still has more.
// //
// // PUT stock-indent/update-requested-quantities
// //   { storageLocation, items: [{ indent, rawMaterial, requestedQuantity, quantity }] }
// //
// // PUT stock-indent/update-fullfillment-status?_id=<indent>   { status }
// //   ONE "Update" tap runs both calls in order. The status is decided by the
// //   app:  every item picked == requested  → "READY"
// //         anything short / not entered    → "PARTIALLY-READY"
// //
// // "Requested" on an editable indent = requestedQuantity - dispatchedQuantity
// // (what is still left to send). Example: requested 2500, already dispatched 1000
// // → the picker sees 1500, and 1500 is the cap / "Full" value.
// // -----------------------------------------------------------------------

// const API = {
//     LIST: "stock-indent/stock-indents",
//     UPDATE_QUANTITIES: "stock-indent/update-requested-quantities",
//     UPDATE_STATUS: "stock-indent/update-fullfillment-status",
// };

// const STATUS_READY = "READY";
// const STATUS_PARTIAL_READY = "PARTIALLY-READY";

// const DRAFT_STORAGE_KEY = "xl-pickup-drafts-v1";

// /* ─── status handling ─────────────────────────────────────── */
// const STATUS_ALIASES = {
//     PARTIALLY_READY: "PARTIAL_READY",
//     PARTIAL_DISPATCH: "PARTIAL_DISPATCHED",
//     PARTIALLY_DISPATCH: "PARTIAL_DISPATCHED",
//     PARTIALLY_DISPATCHED: "PARTIAL_DISPATCHED",
//     DISPATCH: "DISPATCHED",
//     PARTIALLY_RECEIVED: "PARTIAL_RECEIVED",
// };
// const normStatus = (status) => {
//     const key = String(status || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
//     return STATUS_ALIASES[key] || key;
// };

// const STATUS_META = {
//     ACCEPTED: { label: "To pick", fg: "#B26A00", bg: "#FFF7E6", border: "#FCE4B4" },
//     PARTIAL_READY: { label: "Partially ready", fg: "#B26A00", bg: "#FFF7E6", border: "#FCE4B4" },
//     READY: { label: "Ready", fg: "#0A6B58", bg: "#E5F7F3", border: "#B9E9DF" },
//     PARTIAL_DISPATCHED: { label: "Partially dispatched", fg: "#5B3FC4", bg: "#F1ECFD", border: "#D9CCF8" },
//     DISPATCHED: { label: "Dispatched", fg: "#04698F", bg: "#E9F4F9", border: "#CFE6F0" },
//     PARTIAL_RECEIVED: { label: "Partially received", fg: "#B26A00", bg: "#FFF7E6", border: "#FCE4B4" },
//     RECEIVED: { label: "Received", fg: "#0A6B58", bg: "#E5F7F3", border: "#B9E9DF" },
// };
// const getStatusMeta = (status) =>
//     STATUS_META[status] || { label: String(status || "Unknown"), fg: "#5B6B7C", bg: "#F1FAFD", border: "#DCEAF1" };

// // The app never shows these (Pending/Rejected are admin-side, Closed is hidden from the app).
// const HIDDEN_STATUSES = new Set(["PENDING", "REJECTED", "CLOSED"]);

// // Quantities can be entered while an indent is still "To pick" (Accepted), on a
// // Partially Ready one (to pick the rest) and on a Partially Dispatched one — the user
// // enters what they pick and the indent goes to Ready / Partially Ready.
// const EDITABLE_STATUSES = new Set(["ACCEPTED", "PARTIAL_READY", "PARTIAL_DISPATCHED"]);

// const PAGE_SIZE = 500;

// /* ─── small helpers ───────────────────────────────────────── */
// const hasValue = (value) => value !== null && value !== undefined && value !== "";
// const isValidQuantityInput = (value) => /^\d*\.?\d*$/.test(value);
// const getRmId = (item) => item?.rawMaterial?._id || item?._id;
// // Whatever field logistics' picked quantity comes back in (shown on read-only indents).
// const getPickedQty = (item) => item?.readyQuantity ?? item?.dispatchedQuantity ?? item?.pickedQuantity ?? null;

// // Requested qty as raised by the outlet, and what has already been dispatched against it.
// const getOriginalQty = (item) => Number(item?.requestedQuantity || 0);
// const getDispatchedQty = (item) => Number(item?.dispatchedQuantity || 0);
// // New requested = requested − dispatched (never below 0, and unchanged when nothing was dispatched).
// // Rounded to avoid float noise such as 0.30000000000000004.
// const getRemainingQty = (item) =>
//     Math.max(Math.round((getOriginalQty(item) - getDispatchedQty(item)) * 1000) / 1000, 0);

// const formatDate = (iso) => {
//     if (!iso) return "";
//     return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
// };

// const mergeById = (previous, incoming) => {
//     const seen = new Set(previous.map((row) => row._id));
//     return [...previous, ...incoming.filter((row) => !seen.has(row._id))];
// };

// const extractRows = (response) => {
//     const data = response?.data?.data;
//     if (Array.isArray(data)) return data;
//     if (Array.isArray(data?.data)) return data.data;
//     return [];
// };

// /* ─── outlet details (state, city, address, coordinates) ──── */
// const toNumber = (value) => (hasValue(value) ? Number(value) : NaN);
// const validCoords = (lat, lng) =>
//     Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);

// // Tolerates {latitude,longitude}, {lat,lng}, {coordinates:{…}}, {location:{…}} and GeoJSON [lng, lat].
// const getOutletCoords = (outlet = {}) => {
//     const pairs = [
//         [outlet.latitude, outlet.longitude],
//         [outlet.lat, outlet.lng ?? outlet.lon ?? outlet.long],
//         [outlet.coordinates?.latitude, outlet.coordinates?.longitude],
//         [outlet.coordinates?.lat, outlet.coordinates?.lng],
//         [outlet.location?.latitude, outlet.location?.longitude],
//         [outlet.location?.lat, outlet.location?.lng],
//     ];
//     for (const [a, b] of pairs) {
//         const lat = toNumber(a);
//         const lng = toNumber(b);
//         if (validCoords(lat, lng)) return { lat, lng };
//     }
//     const geo = outlet.location?.coordinates || outlet.coordinates;
//     if (Array.isArray(geo) && geo.length >= 2) {
//         const lng = toNumber(geo[0]);
//         const lat = toNumber(geo[1]);
//         if (validCoords(lat, lng)) return { lat, lng };
//     }
//     return null;
// };

// // "Landscape pinto park , " -> "Landscape pinto park"
// const getOutletAddress = (outlet = {}) =>
//     String(outlet.address || "").replace(/\s+/g, " ").replace(/[\s,]+$/, "").trim();

// const getDirectionsUrl = (outlet = {}) => {
//     const coords = getOutletCoords(outlet);
//     const destination = coords
//         ? `${coords.lat},${coords.lng}`
//         : [outlet.name, getOutletAddress(outlet), outlet.city, outlet.state].filter(Boolean).join(", ");
//     if (!destination) return null;
//     return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
// };

// const getOutletPlace = (outlet = {}) => [outlet.city, outlet.state].filter(Boolean).join(", ");

// /* ─── draft persistence (a reload must not wipe typed quantities) ─── */
// const loadDrafts = () => {
//     try {
//         return JSON.parse(window.sessionStorage.getItem(DRAFT_STORAGE_KEY)) || {};
//     } catch (e) {
//         return {};
//     }
// };
// const saveDrafts = (drafts) => {
//     try {
//         window.sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts));
//     } catch (e) {
//         /* storage unavailable — drafts just won't survive a reload */
//     }
// };

// /* ─── per-indent summary + outcome ────────────────────────── */
// // "Full" means the picked qty equals the NEW requested qty (requested − already dispatched).
// const summarize = (indent, draft = {}) => {
//     const items = indent.items || [];
//     let entered = 0;
//     let positive = 0;
//     let full = 0;
//     items.forEach((item) => {
//         const raw = draft[getRmId(item)];
//         if (!hasValue(raw)) return;
//         entered += 1;
//         const quantity = Number(raw);
//         if (quantity > 0) positive += 1;
//         if (quantity === getRemainingQty(item)) full += 1;
//     });
//     const total = items.length;
//     return { total, entered, positive, full, short: total - full, allFull: total > 0 && full === total };
// };

// /* ─── tiny UI pieces ──────────────────────────────────────── */
// const Chevron = ({ open }) => (
//     <svg
//         width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B6B7C" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"
//         className={`shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
//     >
//         <path d="m6 9 6 6 6-6" />
//     </svg>
// );

// const Spinner = ({ className = "border-white/40 border-t-white" }) => (
//     <span className={`w-4 h-4 rounded-full border-2 animate-spin ${className}`} />
// );

// const BalanceChip = ({ balance }) => {
//     const number = Number(balance);
//     if (Number.isNaN(number)) return null;
//     let label = "Complete";
//     let style = { color: "#0A6B58", background: "#E5F7F3" };
//     if (number > 0) {
//         label = `Short ${formatQuantity(number)}`;
//         style = { color: "#B26A00", background: "#FFF7E6" };
//     } else if (number < 0) {
//         label = `Excess ${formatQuantity(Math.abs(number))}`;
//         style = { color: "#04698F", background: "#E9F4F9" };
//     }
//     return (
//         <span className="rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap" style={style}>
//             {label}
//         </span>
//     );
// };

// /* ─── Outlet filter (bottom sheet) ────────────────────────── */
// const OutletSheet = ({ open, onClose, options, totalCount, selectedId, onSelect }) => {
//     const [query, setQuery] = useState("");
//     const closeRef = useRef(onClose);
//     closeRef.current = onClose;

//     useEffect(() => {
//         if (!open) return undefined;
//         setQuery("");
//         const previousOverflow = document.body.style.overflow;
//         document.body.style.overflow = "hidden";
//         const onKey = (event) => {
//             if (event.key === "Escape") closeRef.current();
//         };
//         window.addEventListener("keydown", onKey);
//         return () => {
//             document.body.style.overflow = previousOverflow;
//             window.removeEventListener("keydown", onKey);
//         };
//     }, [open]);

//     if (!open) return null;

//     const q = query.trim().toLowerCase();
//     const list = q
//         ? options.filter((option) =>
//             [option.outlet.name, option.outlet.city, option.outlet.state, option.outlet.address]
//                 .filter(Boolean)
//                 .join(" ")
//                 .toLowerCase()
//                 .includes(q)
//         )
//         : options;

//     const rowClass = (active) =>
//         `w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[#F1FAFD] transition ${active ? "bg-[#F1FAFD]" : ""}`;

//     return (
//         <div className="fixed inset-0 z-[120] flex items-end justify-center" role="dialog" aria-modal="true" aria-label="Filter by outlet">
//             <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-[#08213D]/45" />

//             {/* stopPropagation keeps the page's pull-to-refresh from reacting to sheet scrolling */}
//             <div
//                 onTouchStart={(event) => event.stopPropagation()}
//                 onTouchMove={(event) => event.stopPropagation()}
//                 className="relative w-full max-w-[430px] md:max-w-[680px] lg:max-w-[860px] max-h-[78vh] flex flex-col rounded-t-[28px] bg-white shadow-[0_-20px_50px_rgba(4,33,63,0.25)]"
//             >
//                 <div className="px-5 pt-3 pb-3 border-b border-[#DCEAF1]">
//                     <div className="w-10 h-1 rounded-full bg-[#DCEAF1] mx-auto mb-3" />
//                     <div className="flex items-center justify-between">
//                         <h3 className="xl-display text-[17px] font-bold text-[#08213D]">Filter by outlet</h3>
//                         <button type="button" onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full bg-[#F1FAFD] text-[#5B6B7C] flex items-center justify-center">
//                             <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
//                         </button>
//                     </div>

//                     {options.length > 6 && (
//                         <input
//                             type="search"
//                             value={query}
//                             onChange={(event) => setQuery(event.target.value)}
//                             placeholder="Search outlet or city"
//                             className="xl-body mt-3 w-full h-11 rounded-[14px] bg-[#F9FCFD] border border-[#DCEAF1] px-4 text-sm font-medium text-[#08213D] placeholder:text-[#9FB4BD] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30"
//                         />
//                     )}
//                 </div>

//                 <div className="overflow-y-auto pb-6">
//                     {!q && (
//                         <button type="button" onClick={() => onSelect(null)} className={rowClass(!selectedId)}>
//                             <span className="min-w-0 flex-1">
//                                 <span className="xl-body block text-[14px] font-bold text-[#08213D]">All outlets</span>
//                             </span>
//                             <span className="xl-mono rounded-full bg-[#F1FAFD] border border-[#DCEAF1] px-2.5 py-0.5 text-[10px] font-medium text-[#08213D]">{totalCount}</span>
//                             {!selectedId && <span className="text-[#00A4E3] font-bold">✓</span>}
//                         </button>
//                     )}

//                     {list.map((option) => {
//                         const active = selectedId === option.id;
//                         const place = getOutletPlace(option.outlet);
//                         return (
//                             <button key={option.id} type="button" onClick={() => onSelect(option.id)} className={`${rowClass(active)} border-t border-[#F1FAFD]`}>
//                                 <span className="min-w-0 flex-1">
//                                     <span className="xl-body block text-[14px] font-bold text-[#08213D] truncate">{option.outlet.name || "Unknown outlet"}</span>
//                                     {place && <span className="xl-body block text-[11px] font-medium text-[#5B6B7C] truncate mt-0.5">{place}</span>}
//                                 </span>
//                                 <span className="xl-mono rounded-full bg-[#F1FAFD] border border-[#DCEAF1] px-2.5 py-0.5 text-[10px] font-medium text-[#08213D]">{option.count}</span>
//                                 {active && <span className="text-[#00A4E3] font-bold">✓</span>}
//                             </button>
//                         );
//                     })}

//                     {list.length === 0 && (
//                         <p className="xl-body text-center text-sm font-medium text-[#5B6B7C] py-8">No outlet matches &quot;{query}&quot;</p>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// };

// /* ─── One item row ────────────────────────────────────────── */
// const ItemRow = ({ item, editable, locked, value, overLimit, onChange, onFull, isLast }) => {
//     const rm = item.rawMaterial || {};
//     const unit = formatUnit(rm.unit);
//     const originalRequested = getOriginalQty(item);
//     const dispatchedSoFar = getDispatchedQty(item);
//     // Editable rows show the NEW requested qty (requested − already dispatched).
//     const requested = editable ? getRemainingQty(item) : originalRequested;
//     const showReduction = editable && dispatchedSoFar > 0;
//     const categories = [rm.mainCategoryName, rm.subCategoryName].filter(Boolean).join(" • ");
//     const entered = hasValue(value);
//     const quantity = Number(value);
//     const isFull = entered && quantity === requested;
//     const shortBy = entered && quantity < requested ? requested - quantity : 0;

//     const handleKeyDown = (event) => {
//         if (event.key !== "Enter") return;
//         event.preventDefault();
//         const card = event.currentTarget.closest("[data-indent-card]");
//         const inputs = card ? Array.from(card.querySelectorAll("[data-qty-input]:not(:disabled)")) : [];
//         const next = inputs[inputs.indexOf(event.currentTarget) + 1];
//         if (next) next.focus();
//         else event.currentTarget.blur();
//     };

//     const picked = getPickedQty(item);
//     const received = hasValue(item.totalReceivedQuantity);

//     return (
//         <div className={`py-3 ${isLast ? "" : "border-b border-[#F1FAFD]"}`}>
//             <div className="flex items-start justify-between gap-3">
//                 <div className="min-w-0">
//                     <p className="xl-body text-[13px] font-semibold text-[#08213D] leading-snug break-words">{(rm.name || "—").trim()}</p>
//                     {categories && <p className="xl-body text-[10px] font-medium text-[#5B6B7C] mt-0.5">{categories}</p>}
//                 </div>
//                 <div className="shrink-0 text-right">
//                     <p className="xl-mono text-[8px] font-medium uppercase tracking-wide text-[#5B6B7C]">Requested</p>
//                     <p className="xl-display text-[15px] font-bold text-[#08213D] whitespace-nowrap leading-tight">
//                         {formatQuantity(requested)}
//                         <span className="xl-body text-[11px] font-semibold text-[#5B6B7C] ml-1">{unit}</span>
//                     </p>
//                     {showReduction && (
//                         <p className="xl-body text-[10px] font-medium text-[#5B6B7C] mt-0.5 whitespace-nowrap">
//                             {formatQuantity(originalRequested)} − {formatQuantity(dispatchedSoFar)} dispatched
//                         </p>
//                     )}
//                 </div>
//             </div>

//             {editable && hasValue(picked) && (
//                 <p className="xl-body text-[11px] font-semibold text-[#5B6B7C] mt-1.5">
//                     Already picked {formatQuantity(picked)} {unit}
//                 </p>
//             )}

//             {editable ? (
//                 <>
//                     <div className="flex items-center gap-2 mt-2.5">
//                         <div className="relative flex-1">
//                             <input
//                                 data-qty-input
//                                 type="text"
//                                 inputMode="decimal"
//                                 enterKeyHint="next"
//                                 autoComplete="off"
//                                 value={value}
//                                 disabled={locked}
//                                 onChange={(event) => onChange(event.target.value)}
//                                 onFocus={(event) => event.target.select()}
//                                 onKeyDown={handleKeyDown}
//                                 placeholder="Qty picked"
//                                 aria-label={`Quantity picked for ${rm.name || "item"}`}
//                                 className={`xl-display w-full h-12 rounded-[14px] border pl-4 pr-16 text-[17px] font-bold focus:outline-none focus:ring-2 disabled:opacity-60 ${overLimit
//                                     ? "border-red-300 bg-red-50 text-red-600 focus:ring-red-200"
//                                     : isFull
//                                         ? "border-[#B9E9DF] bg-[#F1FAF8] text-[#0A6B58] focus:ring-[#00A88C]/30"
//                                         : entered
//                                             ? "border-[#FCE4B4] bg-[#FFFBF2] text-[#08213D] focus:ring-[#E08A3C]/30"
//                                             : "border-[#DCEAF1] bg-[#F9FCFD] text-[#08213D] focus:ring-[#00A4E3]/30"
//                                     }`}
//                             />
//                             <span className="xl-body absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#5B6B7C]">{unit}</span>
//                         </div>

//                         <button
//                             type="button"
//                             onClick={onFull}
//                             disabled={locked}
//                             className={`xl-body h-12 px-3.5 rounded-[14px] text-[12px] font-bold border transition active:scale-95 disabled:opacity-60 ${isFull
//                                 ? "bg-[#00A88C] border-[#00A88C] text-white"
//                                 : "bg-white border-[#DCEAF1] text-[#00A4E3]"
//                                 }`}
//                         >
//                             {isFull ? "✓ Full" : "Full"}
//                         </button>
//                     </div>

//                     {(overLimit || isFull || shortBy > 0) && (
//                         <p className={`xl-body text-[10px] font-semibold mt-1.5 ${overLimit ? "text-red-500" : isFull ? "text-[#00A88C]" : "text-amber-600"}`}>
//                             {overLimit
//                                 ? `Can't be more than requested (${formatQuantity(requested)} ${unit})`
//                                 : isFull
//                                     ? "Matches requested qty"
//                                     : `Short by ${formatQuantity(shortBy)} ${unit}`}
//                         </p>
//                     )}
//                 </>
//             ) : (
//                 (hasValue(picked) || received) && (
//                     <div className="flex items-center justify-between gap-3 mt-1.5">
//                         <p className="xl-body text-[11px] font-semibold text-[#5B6B7C]">
//                             {hasValue(picked) && <>Picked {formatQuantity(picked)} {unit}</>}
//                             {hasValue(picked) && received && " · "}
//                             {received && <>Received {formatQuantity(item.totalReceivedQuantity)} {unit}</>}
//                         </p>
//                         {received && hasValue(item.balanceQuantity) && <BalanceChip balance={item.balanceQuantity} />}
//                     </div>
//                 )
//             )}
//         </div>
//     );
// };

// /* ─── One indent card ─────────────────────────────────────── */
// const IndentCard = ({
//     indent, isOpen, onToggle, draft, onQtyChange, onFillAll, onClear, overKey,
//     submitting, retryStatus, cardError, onSubmit,
// }) => {
//     const status = normStatus(indent.status);
//     const meta = getStatusMeta(status);
//     const items = indent.items || [];
//     const editable = EDITABLE_STATUSES.has(status);
//     const locked = submitting || !!retryStatus; // quantities already saved → don't allow edits
//     const summary = summarize(indent, draft);
//     const hasDraft = summary.entered > 0;
//     const willBeReady = retryStatus ? retryStatus === STATUS_READY : summary.allFull;
//     const canSubmit = !!retryStatus || summary.positive > 0;

//     return (
//         <div data-indent-card className="rounded-[22px] bg-white border border-[#DCEAF1]">
//             <button
//                 type="button"
//                 onClick={onToggle}
//                 aria-expanded={isOpen}
//                 className="w-full flex items-center justify-between gap-3 px-4 py-4 active:opacity-70 transition"
//             >
//                 <div className="min-w-0 text-left">
//                     <div className="flex items-center gap-2 flex-wrap">
//                         <span className="xl-mono text-[13px] font-bold text-[#00A4E3]">{indent.indentNumber}</span>
//                         <span
//                             className="xl-body rounded-full border px-2.5 py-0.5 text-[10px] font-bold"
//                             style={{ color: meta.fg, background: meta.bg, borderColor: meta.border }}
//                         >
//                             {meta.label}
//                         </span>
//                         {editable && hasDraft && (
//                             <span className="xl-body rounded-full bg-[#E9F4F9] px-2 py-0.5 text-[10px] font-bold text-[#04698F]">
//                                 {summary.entered}/{summary.total} filled
//                             </span>
//                         )}
//                     </div>
//                     <p className="xl-body text-xs font-medium text-[#5B6B7C] mt-1 truncate">
//                         {items.length} item{items.length === 1 ? "" : "s"} · {formatDate(indent.createdAt)}
//                     </p>
//                 </div>
//                 <Chevron open={isOpen} />
//             </button>

//             {isOpen && (
//                 <div className="border-t border-dashed border-[#DCEAF1]">
//                     <div className="px-4">
//                         {editable && items.length > 0 && (
//                             <div className="flex items-center justify-between pt-3">
//                                 <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-[#5B6B7C]">Enter what you picked</p>
//                                 <div className="flex items-center gap-3">
//                                     <button type="button" onClick={onFillAll} disabled={locked} className="xl-body text-[11px] font-bold text-[#00A4E3] active:opacity-60 disabled:opacity-40">
//                                         Fill all as requested
//                                     </button>
//                                     {hasDraft && !locked && (
//                                         <button type="button" onClick={onClear} className="xl-body text-[11px] font-bold text-[#5B6B7C] active:opacity-60">
//                                             Clear
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         )}

//                         {items.length === 0 ? (
//                             <p className="xl-body text-xs font-medium text-[#5B6B7C] py-3">No items on this indent</p>
//                         ) : (
//                             items.map((item, idx) => {
//                                 const rmId = getRmId(item);
//                                 return (
//                                     <ItemRow
//                                         key={item._id || rmId || idx}
//                                         item={item}
//                                         editable={editable}
//                                         locked={locked}
//                                         value={draft?.[rmId] ?? ""}
//                                         overLimit={overKey === `${indent._id}:${rmId}`}
//                                         onChange={(value) => onQtyChange(item, value)}
//                                         onFull={() => {
//                                             const remaining = getRemainingQty(item);
//                                             const isFull = hasValue(draft?.[rmId]) && Number(draft[rmId]) === remaining;
//                                             onQtyChange(item, isFull ? "" : String(remaining));
//                                         }}
//                                         isLast={idx === items.length - 1}
//                                     />
//                                 );
//                             })
//                         )}
//                     </div>

//                     {editable && items.length > 0 && (
//                         <div className="px-3 pb-3 pt-2">
//                             <div className="rounded-[18px] bg-[#F9FCFD] border border-[#DCEAF1] p-3">
//                                 {cardError && (
//                                     <p className="xl-body text-[11px] font-semibold text-red-500 mb-2 leading-snug">{cardError}</p>
//                                 )}

//                                 {canSubmit && (
//                                     <p className="xl-body text-[11px] font-semibold mb-2" style={{ color: willBeReady ? "#0A6B58" : "#B26A00" }}>
//                                         {retryStatus
//                                             ? "Quantities are saved. Only the status update is left."
//                                             : willBeReady
//                                                 ? "All items complete — this indent will be marked Ready"
//                                                 : `${summary.short} item${summary.short === 1 ? " is" : "s are"} short or not entered — this indent will be marked Partially Ready`}
//                                     </p>
//                                 )}

//                                 <button
//                                     type="button"
//                                     onClick={onSubmit}
//                                     disabled={!canSubmit || submitting}
//                                     className={`xl-display w-full h-12 rounded-[14px] text-sm font-bold flex items-center justify-center gap-2 transition active:scale-[0.98] ${canSubmit && !submitting ? "text-white" : "bg-[#E9F4F9] text-[#9FB4BD] cursor-not-allowed"}`}
//                                     style={
//                                         canSubmit && !submitting
//                                             ? {
//                                                 background: willBeReady
//                                                     ? "linear-gradient(135deg, #00A88C 0%, #04806D 100%)"
//                                                     : "linear-gradient(135deg, #E08A3C 0%, #C26A12 100%)",
//                                                 boxShadow: "0 10px 24px rgba(4,33,63,0.22)",
//                                             }
//                                             : undefined
//                                     }
//                                 >
//                                     {submitting ? (
//                                         <><Spinner /> Updating…</>
//                                     ) : !canSubmit ? (
//                                         "Enter quantities to update"
//                                     ) : retryStatus ? (
//                                         "Retry status update"
//                                     ) : willBeReady ? (
//                                         "Mark as Ready"
//                                     ) : (
//                                         "Mark as Partially Ready"
//                                     )}
//                                 </button>
//                             </div>
//                         </div>
//                     )}
//                 </div>
//             )}
//         </div>
//     );
// };

// /* ═══════════════════════════════════════════════════════════
//    MAIN
//    ═══════════════════════════════════════════════════════════ */
// export default function OutletWiseList({ onNotify }) {
//     const storageLocation = useSelector(selectStorageLocation);
//     const storageLocationId = storageLocation?._id;
//     const notify = useCallback((type, message) => { if (onNotify) onNotify(type, message); }, [onNotify]);

//     const [indents, setIndents] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [refreshing, setRefreshing] = useState(false);
//     const [error, setError] = useState("");

//     const [pagination, setPagination] = useState(null);
//     const [loadingMore, setLoadingMore] = useState(false);
//     const [moreError, setMoreError] = useState("");
//     const [outletId, setOutletId] = useState(null);
//     const [sheetOpen, setSheetOpen] = useState(false);
//     const [searchText, setSearchText] = useState("");
//     const [collapsedOutlets, setCollapsedOutlets] = useState(() => new Set());
//     const [openIndents, setOpenIndents] = useState(() => new Set());

//     // Typed quantities: { [indentId]: { [rawMaterialId]: "12.5" } }
//     const [drafts, setDrafts] = useState(loadDrafts);
//     const [overKey, setOverKey] = useState(null);
//     const overTimerRef = useRef(null);

//     const [submittingId, setSubmittingId] = useState(null);
//     const [retryStatus, setRetryStatus] = useState({}); // { [indentId]: "READY" | "PARTIALLY-READY" } — quantities saved, status pending
//     const [cardErrors, setCardErrors] = useState({});

//     const requestRef = useRef(0);

//     useEffect(() => () => { if (overTimerRef.current) window.clearTimeout(overTimerRef.current); }, []);
//     useEffect(() => { saveDrafts(drafts); }, [drafts]);

//     /* ─── load ─── */
//     // Indents still waiting to be picked first (oldest first), then everything else.
//     const sortRows = (rows) =>
//         [...rows].sort((a, b) => {
//             const aRank = EDITABLE_STATUSES.has(normStatus(a.status)) ? 0 : 1;
//             const bRank = EDITABLE_STATUSES.has(normStatus(b.status)) ? 0 : 1;
//             if (aRank !== bRank) return aRank - bRank;
//             return new Date(a.createdAt) - new Date(b.createdAt);
//         });

//     const fetchIndents = useCallback(async (mode = "initial", page = 1) => {
//         const requestId = ++requestRef.current;
//         try {
//             if (mode === "initial") setLoading(true);
//             if (mode === "refresh") setRefreshing(true);
//             if (mode === "more") setLoadingMore(true);
//             if (mode !== "silent") { setError(""); setMoreError(""); }

//             const response = await axiosInstance.get(API.LIST, { params: { page, limit: PAGE_SIZE } });
//             if (requestId !== requestRef.current) return;

//             const incoming = extractRows(response)
//                 // Belt and braces: only this staff member's storage location.
//                 .filter((row) => !storageLocationId || !row.storageLocation?._id || row.storageLocation._id === storageLocationId)
//                 .filter((row) => !HIDDEN_STATUSES.has(normStatus(row.status)));

//             setPagination(response?.data?.pagination || null);
//             setIndents((previous) => sortRows(mode === "more" ? mergeById(previous, incoming) : incoming));

//             // Drop saved drafts for indents the server now says are no longer pickable.
//             const settled = new Set(incoming.filter((row) => !EDITABLE_STATUSES.has(normStatus(row.status))).map((row) => row._id));
//             setDrafts((previous) => {
//                 let changed = false;
//                 const next = {};
//                 Object.keys(previous).forEach((id) => {
//                     if (settled.has(id)) changed = true;
//                     else next[id] = previous[id];
//                 });
//                 return changed ? next : previous;
//             });
//         } catch (err) {
//             if (requestId !== requestRef.current) return;
//             console.error("Indent list API error:", err?.response || err);
//             const message = getErrorMessage(err, "Unable to load indents. Please try again.");
//             if (mode === "more") {
//                 setMoreError(message); // keep what's already on screen
//             } else if (mode !== "silent") {
//                 setError(message);
//                 setIndents([]);
//                 setPagination(null);
//             }
//         } finally {
//             if (requestId === requestRef.current) {
//                 setLoading(false);
//                 setRefreshing(false);
//                 setLoadingMore(false);
//             }
//         }
//     }, [storageLocationId]);

//     useEffect(() => { fetchIndents("initial"); }, [fetchIndents]);

//     const currentPage = pagination?.page || 1;
//     const hasMore = !!pagination && (pagination.hasNextPage ?? currentPage < (pagination.totalPages || 1));

//     /* ─── derived lists ─── */
//     const searched = useMemo(() => {
//         const query = searchText.trim().toLowerCase();
//         if (!query) return indents;
//         return indents.filter((indent) => {
//             const haystack = [
//                 indent.indentNumber,
//                 indent?.outlet?.name,
//                 indent?.outlet?.city,
//                 ...(indent.items || []).map((item) => item?.rawMaterial?.name),
//             ].filter(Boolean).join(" ").toLowerCase();
//             return haystack.includes(query);
//         });
//     }, [indents, searchText]);

//     // Outlet options come from the indents currently loaded, so every option has something to show.
//     const outletOptions = useMemo(() => {
//         const map = new Map();
//         searched.forEach((indent) => {
//             const outlet = indent.outlet || {};
//             const id = outlet._id || "unknown";
//             if (!map.has(id)) map.set(id, { id, outlet, count: 0 });
//             map.get(id).count += 1;
//         });
//         return [...map.values()].sort((a, b) => (a.outlet.name || "").localeCompare(b.outlet.name || ""));
//     }, [searched]);

//     const filtered = useMemo(
//         () => (outletId ? searched.filter((indent) => (indent.outlet?._id || "unknown") === outletId) : searched),
//         [searched, outletId]
//     );

//     const groups = useMemo(() => {
//         const map = new Map();
//         filtered.forEach((indent) => {
//             const outlet = indent.outlet || {};
//             const id = outlet._id || "unknown";
//             if (!map.has(id)) map.set(id, { id, outlet, indents: [] });
//             map.get(id).indents.push(indent);
//         });
//         return [...map.values()].sort((a, b) => (a.outlet.name || "").localeCompare(b.outlet.name || ""));
//     }, [filtered]);

//     const selectedOutlet = outletId ? outletOptions.find((option) => option.id === outletId)?.outlet || null : null;

//     // A single indent on screen? Open it so the user can start typing straight away.
//     const soloIndentId = filtered.length === 1 ? filtered[0]._id : null;
//     useEffect(() => {
//         if (soloIndentId) setOpenIndents((previous) => (previous.has(soloIndentId) ? previous : new Set(previous).add(soloIndentId)));
//     }, [soloIndentId]);

//     const toggle = (setter, id) =>
//         setter((previous) => {
//             const next = new Set(previous);
//             if (next.has(id)) next.delete(id);
//             else next.add(id);
//             return next;
//         });

//     /* ─── quantity entry ─── */
//     const setQuantity = (indent, item, value) => {
//         const rmId = getRmId(item);
//         const key = `${indent._id}:${rmId}`;
//         if (!isValidQuantityInput(value)) return;

//         // Hard cap at the NEW requested quantity (requested − already dispatched) — flag it inline instead of a noisy toast.
//         if (value !== "" && Number(value) > getRemainingQty(item)) {
//             setOverKey(key);
//             if (overTimerRef.current) window.clearTimeout(overTimerRef.current);
//             overTimerRef.current = window.setTimeout(() => setOverKey(null), 2200);
//             return;
//         }

//         setOverKey(null);
//         setCardErrors((previous) => (previous[indent._id] ? { ...previous, [indent._id]: "" } : previous));
//         setDrafts((previous) => {
//             const forIndent = { ...(previous[indent._id] || {}) };
//             if (value === "") delete forIndent[rmId];
//             else forIndent[rmId] = value;
//             const next = { ...previous };
//             if (Object.keys(forIndent).length === 0) delete next[indent._id];
//             else next[indent._id] = forIndent;
//             return next;
//         });
//     };

//     const fillAll = (indent) => {
//         const forIndent = {};
//         (indent.items || []).forEach((item) => { forIndent[getRmId(item)] = String(getRemainingQty(item)); });
//         setDrafts((previous) => ({ ...previous, [indent._id]: forIndent }));
//     };

//     const clearDraft = (indent) =>
//         setDrafts((previous) => {
//             const next = { ...previous };
//             delete next[indent._id];
//             return next;
//         });

//     /* ─── submit: save quantities, then set Ready / Partially Ready ─── */
//     const handleSubmit = async (indent) => {
//         if (submittingId) return;
//         const draft = drafts[indent._id] || {};
//         const summary = summarize(indent, draft);
//         const pendingStatus = retryStatus[indent._id];
//         if (!pendingStatus && summary.positive === 0) return;

//         const targetStatus = pendingStatus || (summary.allFull ? STATUS_READY : STATUS_PARTIAL_READY);
//         const setCardError = (message) => setCardErrors((previous) => ({ ...previous, [indent._id]: message }));

//         setSubmittingId(indent._id);
//         setCardError("");

//         // Step 1 — quantities (skipped when only the status step is being retried)
//         if (!pendingStatus) {
//             try {
//                 await axiosInstance.put(API.UPDATE_QUANTITIES, {
//                     storageLocation: indent.storageLocation?._id || storageLocationId,
//                     items: (indent.items || [])
//                         .filter((item) => hasValue(draft[getRmId(item)]))
//                         .map((item) => ({
//                             indent: indent._id,
//                             rawMaterial: getRmId(item),
//                             // Still the original requested qty from the server — only the screen shows requested − dispatched.
//                             requestedQuantity: getOriginalQty(item),
//                             quantity: Number(draft[getRmId(item)]),
//                         })),
//                 });
//             } catch (err) {
//                 console.error("Update quantities error:", err?.response || err);
//                 const message = getErrorMessage(err, "Couldn't save the quantities. Nothing was changed — please try again.");
//                 setCardError(message);
//                 notify("error", message);
//                 setSubmittingId(null);
//                 return;
//             }
//         }

//         // Step 2 — status
//         // The API rejects "already PARTIALLY-READY" (and "already READY"), so when the indent is
//         // already at the target status the quantities saved in step 1 are all that is needed.
//         const alreadyAtTarget = normStatus(indent.status) === normStatus(targetStatus);
//         if (!alreadyAtTarget) {
//             try {
//                 await axiosInstance.put(API.UPDATE_STATUS, { status: targetStatus }, { params: { _id: indent._id } });
//             } catch (err) {
//                 console.error("Update status error:", err?.response || err);
//                 setRetryStatus((previous) => ({ ...previous, [indent._id]: targetStatus }));
//                 const message = `Quantities saved, but the status wasn't updated. ${getErrorMessage(err, "Please retry.")}`;
//                 setCardError(message);
//                 notify("error", message);
//                 setSubmittingId(null);
//                 return;
//             }
//         }

//         // Done — update the screen immediately, then quietly re-sync with the server.
//         setIndents((previous) => previous.map((row) => (row._id === indent._id ? { ...row, status: targetStatus } : row)));
//         clearDraft(indent);
//         setRetryStatus((previous) => { const next = { ...previous }; delete next[indent._id]; return next; });
//         setCardErrors((previous) => { const next = { ...previous }; delete next[indent._id]; return next; });
//         setOpenIndents((previous) => { const next = new Set(previous); next.delete(indent._id); return next; });
//         notify("success", `${indent.indentNumber} marked ${targetStatus === STATUS_READY ? "Ready" : "Partially Ready"}`);
//         setSubmittingId(null);
//         fetchIndents("silent");
//     };

//     /* ─── render ─── */
//     const emptyTitle = "Nothing to pick right now";
//     const toPickCount = filtered.filter((indent) => EDITABLE_STATUSES.has(normStatus(indent.status))).length;

//     return (
//         <div>
//             {/* Search */}
//             <div className="relative mb-3">
//                 <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00A4E3]">
//                     <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//                         <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
//                     </svg>
//                 </span>
//                 <input
//                     type="search"
//                     inputMode="search"
//                     value={searchText}
//                     onChange={(event) => setSearchText(event.target.value)}
//                     placeholder="Search indent no., outlet, or item"
//                     className="xl-body w-full h-12 rounded-[16px] bg-white border border-[#DCEAF1] pl-11 pr-10 text-sm font-medium text-[#08213D] placeholder:text-[#9FB4BD] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30"
//                 />
//                 {searchText && (
//                     <button type="button" onClick={() => setSearchText("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#F1FAFD] text-[#5B6B7C] flex items-center justify-center">
//                         <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
//                     </button>
//                 )}
//             </div>

//             {/* Outlet filter + refresh */}
//             {!loading && !error && (
//                 <div className="flex items-center gap-2 mb-4">
//                     <button
//                         type="button"
//                         onClick={() => setSheetOpen(true)}
//                         className={`xl-body flex-1 min-w-0 h-11 rounded-[14px] border px-3.5 flex items-center gap-2.5 text-left active:scale-[0.99] transition ${outletId ? "bg-[#E9F4F9] border-[#00A4E3]/40" : "bg-white border-[#DCEAF1]"}`}
//                     >
//                         <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#04698F" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
//                             <path d="M3 9.5 4.5 4h15L21 9.5M3 9.5a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M5 12.5V20h14v-7.5" />
//                         </svg>
//                         <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-[#08213D]">
//                             {outletId ? selectedOutlet?.name || "Outlet" : "All outlets"}
//                         </span>
//                         <span className="xl-mono shrink-0 text-[10px] font-medium text-[#5B6B7C]">{outletOptions.length}</span>
//                         <Chevron open />
//                     </button>

//                     {outletId && (
//                         <button type="button" onClick={() => setOutletId(null)} className="xl-body h-11 px-3.5 rounded-[14px] bg-white border border-[#DCEAF1] text-[11px] font-bold text-[#5B6B7C] active:scale-95 transition">
//                             Clear
//                         </button>
//                     )}

//                     <button
//                         type="button"
//                         onClick={() => fetchIndents("refresh")}
//                         disabled={refreshing || loading}
//                         aria-label="Refresh list"
//                         className="w-11 h-11 shrink-0 rounded-[14px] bg-white border border-[#DCEAF1] flex items-center justify-center text-[#00A4E3] active:scale-95 transition disabled:opacity-50"
//                     >
//                         <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={refreshing ? "animate-spin" : ""}>
//                             <path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" />
//                         </svg>
//                     </button>
//                 </div>
//             )}

//             {/* Body */}
//             {loading ? (
//                 <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-8 text-center">
//                     <div className="w-10 h-10 mx-auto rounded-full border-[3px] border-[#00A4E3]/20 border-t-[#00A4E3] animate-spin" />
//                     <p className="xl-body mt-4 text-sm font-semibold text-[#08213D]">Loading indents</p>
//                 </div>
//             ) : error ? (
//                 <div className="rounded-[24px] bg-red-50 border border-red-100 p-6 text-center">
//                     <p className="xl-body text-sm font-semibold text-red-500">{error}</p>
//                     <button type="button" onClick={() => fetchIndents("initial")} className="xl-body mt-4 px-6 h-11 rounded-[14px] bg-red-500 text-white text-xs font-semibold active:scale-95 transition">
//                         Try again
//                     </button>
//                 </div>
//             ) : groups.length === 0 ? (
//                 <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-8 text-center">
//                     <p className="xl-body text-sm font-semibold text-[#08213D]">
//                         {searchText ? `No indents match "${searchText}"` : outletId ? "No indents for this outlet" : emptyTitle}
//                     </p>
//                     {!searchText && !outletId && (
//                         <p className="xl-body text-xs font-medium text-[#5B6B7C] mt-1">New requests will show up here automatically</p>
//                     )}
//                     {(searchText || outletId) && (
//                         <button
//                             type="button"
//                             onClick={() => { setSearchText(""); setOutletId(null); }}
//                             className="xl-body mt-4 px-6 h-11 rounded-[14px] text-white text-xs font-semibold active:scale-95 transition"
//                             style={{ background: "#00A4E3" }}
//                         >
//                             {outletId ? "Show all outlets" : "Clear search"}
//                         </button>
//                     )}
//                 </div>
//             ) : (
//                 <div className="space-y-5">
//                     <p className="xl-body text-xs font-semibold text-[#5B6B7C]">
//                         {filtered.length} indent{filtered.length === 1 ? "" : "s"} · {groups.length} outlet{groups.length === 1 ? "" : "s"}
//                         {toPickCount > 0 && ` · ${toPickCount} to pick`}
//                     </p>

//                     {groups.map((group) => {
//                         const { outlet } = group;
//                         const collapsed = collapsedOutlets.has(group.id);
//                         const place = getOutletPlace(outlet);
//                         const directionsUrl = getDirectionsUrl(outlet);
//                         const address = getOutletAddress(outlet);

//                         return (
//                             <section key={group.id}>
//                                 <div className="flex items-start gap-2 pb-2.5">
//                                     <button
//                                         type="button"
//                                         onClick={() => toggle(setCollapsedOutlets, group.id)}
//                                         aria-expanded={!collapsed}
//                                         className="flex-1 min-w-0 flex items-start gap-3 px-1 text-left active:opacity-70 transition"
//                                     >
//                                         <span className="w-10 h-10 shrink-0 rounded-[14px] bg-[#E9F4F9] text-[#04698F] flex items-center justify-center">
//                                             <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
//                                                 <path d="M3 9.5 4.5 4h15L21 9.5M3 9.5a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M5 12.5V20h14v-7.5" />
//                                             </svg>
//                                         </span>
//                                         <span className="min-w-0 flex-1">
//                                             <span className="xl-display block text-[16px] font-bold text-[#08213D] leading-tight truncate">{outlet.name || "Unknown outlet"}</span>
//                                             <span className="xl-body block text-[11px] font-medium text-[#5B6B7C] mt-0.5 truncate">
//                                                 {place ? `${place} · ` : ""}{group.indents.length} indent{group.indents.length === 1 ? "" : "s"}
//                                             </span>
//                                             {address && (
//                                                 <span className="xl-body block text-[11px] font-medium text-[#9FB4BD] mt-0.5 line-clamp-2">{address}</span>
//                                             )}
//                                         </span>
//                                         <span className="pt-1"><Chevron open={!collapsed} /></span>
//                                     </button>

//                                     {directionsUrl && (
//                                         <a
//                                             href={directionsUrl}
//                                             target="_blank"
//                                             rel="noopener noreferrer"
//                                             aria-label={`Directions to ${outlet.name || "outlet"}`}
//                                             className="xl-body shrink-0 h-9 px-3 rounded-full bg-white border border-[#DCEAF1] text-[11px] font-bold text-[#04698F] flex items-center gap-1.5 active:scale-95 transition"
//                                         >
//                                             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
//                                                 <path d="m3 11 18-8-8 18-2-8-8-2Z" />
//                                             </svg>
//                                             Directions
//                                         </a>
//                                     )}
//                                 </div>

//                                 {!collapsed && (
//                                     <div className="space-y-3">
//                                         {group.indents.map((indent) => (
//                                             <IndentCard
//                                                 key={indent._id}
//                                                 indent={indent}
//                                                 isOpen={openIndents.has(indent._id)}
//                                                 onToggle={() => toggle(setOpenIndents, indent._id)}
//                                                 draft={drafts[indent._id]}
//                                                 onQtyChange={(item, value) => setQuantity(indent, item, value)}
//                                                 onFillAll={() => fillAll(indent)}
//                                                 onClear={() => clearDraft(indent)}
//                                                 overKey={overKey}
//                                                 submitting={submittingId === indent._id}
//                                                 retryStatus={retryStatus[indent._id]}
//                                                 cardError={cardErrors[indent._id]}
//                                                 onSubmit={() => handleSubmit(indent)}
//                                             />
//                                         ))}
//                                     </div>
//                                 )}
//                             </section>
//                         );
//                     })}

//                     {(hasMore || moreError) && (
//                         <div className="text-center">
//                             {moreError && <p className="xl-body text-xs font-semibold text-red-500 mb-2">{moreError}</p>}
//                             <button
//                                 type="button"
//                                 onClick={() => fetchIndents("more", currentPage + 1)}
//                                 disabled={loadingMore}
//                                 className="xl-body h-11 px-6 rounded-[14px] bg-white border border-[#DCEAF1] text-xs font-bold text-[#04698F] active:scale-95 transition disabled:opacity-60 inline-flex items-center gap-2"
//                             >
//                                 {loadingMore && <Spinner className="border-[#04698F]/25 border-t-[#04698F]" />}
//                                 {loadingMore ? "Loading…" : moreError ? "Try again" : "Load more"}
//                             </button>
//                         </div>
//                     )}
//                 </div>
//             )}

//             <OutletSheet
//                 open={sheetOpen}
//                 onClose={() => setSheetOpen(false)}
//                 options={outletOptions}
//                 totalCount={searched.length}
//                 selectedId={outletId}
//                 onSelect={(id) => { setOutletId(id); setSheetOpen(false); }}
//             />
//         </div>
//     );
// }




import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../utils/Api";
import { selectStorageLocation } from "../store/authSlice";
import { formatQuantity, formatUnit, getErrorMessage } from "../utils/format";

// ---- APIs used on this screen ----------------------------------------
// GET stock-indent/stock-indents
//   ?page=1&limit=500
//   -> { success, data: [{ _id, indentNumber, status, createdAt, updatedAt,
//        storageLocation: {...}, outlet: {_id,name,city,state,address,
//        coordinates:{latitude,longitude}, location:{coordinates:[lng,lat]}},
//        items: [{ _id, rawMaterial: {...}, requestedQuantity,
//                  dispatchedQuantity, totalReceivedQuantity, ... }] }],
//        pagination: { page, limit, totalRecords, totalPages, hasNextPage } }
//   One big page is fetched so the outlet filter + search work instantly on
//   the client; "Load more" appears only if the server still has more.
//
// PUT stock-indent/update-requested-quantities
//   { storageLocation, items: [{ indent, rawMaterial, requestedQuantity, quantity }] }
//
// PUT stock-indent/update-fullfillment-status?_id=<indent>   { status }
//   ONE "Update" tap runs both calls in order. The status is decided by the
//   app:  every item picked == requested  → "READY"
//         anything short / not entered    → "PARTIALLY-READY"
//
// "Requested" on an editable indent = requestedQuantity - dispatchedQuantity
// (what is still left to send). Example: requested 2500, already dispatched 1000
// → the picker sees 1500, and 1500 is the cap / "Full" value.
// Items whose remaining quantity is 0 are hidden on editable indents.
//
// Ordering: latest updatedAt first (falls back to createdAt); RECEIVED indents
// always go to the end, regardless of updatedAt.
// -----------------------------------------------------------------------

const API = {
    LIST: "stock-indent/stock-indents",
    UPDATE_QUANTITIES: "stock-indent/update-requested-quantities",
    UPDATE_STATUS: "stock-indent/update-fullfillment-status",
};

const STATUS_READY = "READY";
const STATUS_PARTIAL_READY = "PARTIALLY-READY";

const DRAFT_STORAGE_KEY = "xl-pickup-drafts-v1";

/* ─── status handling ─────────────────────────────────────── */
const STATUS_ALIASES = {
    PARTIALLY_READY: "PARTIAL_READY",
    PARTIAL_DISPATCH: "PARTIAL_DISPATCHED",
    PARTIALLY_DISPATCH: "PARTIAL_DISPATCHED",
    PARTIALLY_DISPATCHED: "PARTIAL_DISPATCHED",
    DISPATCH: "DISPATCHED",
    PARTIALLY_RECEIVED: "PARTIAL_RECEIVED",
};
const normStatus = (status) => {
    const key = String(status || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
    return STATUS_ALIASES[key] || key;
};

const STATUS_META = {
    ACCEPTED: { label: "To pick", fg: "#B26A00", bg: "#FFF7E6", border: "#FCE4B4" },
    PARTIAL_READY: { label: "Partially ready", fg: "#B26A00", bg: "#FFF7E6", border: "#FCE4B4" },
    READY: { label: "Ready", fg: "#0A6B58", bg: "#E5F7F3", border: "#B9E9DF" },
    PARTIAL_DISPATCHED: { label: "Partially dispatched", fg: "#5B3FC4", bg: "#F1ECFD", border: "#D9CCF8" },
    DISPATCHED: { label: "Dispatched", fg: "#04698F", bg: "#E9F4F9", border: "#CFE6F0" },
    PARTIAL_RECEIVED: { label: "Partially received", fg: "#B26A00", bg: "#FFF7E6", border: "#FCE4B4" },
    RECEIVED: { label: "Received", fg: "#0A6B58", bg: "#E5F7F3", border: "#B9E9DF" },
};
const getStatusMeta = (status) =>
    STATUS_META[status] || { label: String(status || "Unknown"), fg: "#5B6B7C", bg: "#F1FAFD", border: "#DCEAF1" };

// The app never shows these (Pending/Rejected are admin-side, Closed is hidden from the app).
const HIDDEN_STATUSES = new Set(["PENDING", "REJECTED", "CLOSED"]);

// Quantities can be entered while an indent is still "To pick" (Accepted), on a
// Partially Ready one (to pick the rest), on a Partially Dispatched one and on a
// Partially Received one — the user enters what they pick and the indent goes to
// Ready / Partially Ready.
const EDITABLE_STATUSES = new Set(["ACCEPTED", "PARTIAL_READY", "PARTIAL_DISPATCHED", "PARTIAL_RECEIVED"]);

const PAGE_SIZE = 500;

// "New" badge = indent updated within the last 24 hours.
const NEW_BADGE_WINDOW_MS = 24 * 60 * 60 * 1000;

/* ─── small helpers ───────────────────────────────────────── */
const hasValue = (value) => value !== null && value !== undefined && value !== "";
const isValidQuantityInput = (value) => /^\d*\.?\d*$/.test(value);
const getRmId = (item) => item?.rawMaterial?._id || item?._id;
// Whatever field logistics' picked quantity comes back in (shown on read-only indents).
const getPickedQty = (item) => item?.readyQuantity ?? item?.dispatchedQuantity ?? item?.pickedQuantity ?? null;

// Requested qty as raised by the outlet, and what has already been dispatched against it.
const getOriginalQty = (item) => Number(item?.requestedQuantity || 0);
const getDispatchedQty = (item) => Number(item?.dispatchedQuantity || 0);
// New requested = requested − dispatched (never below 0, and unchanged when nothing was dispatched).
// Rounded to avoid float noise such as 0.30000000000000004.
const getRemainingQty = (item) =>
    Math.max(Math.round((getOriginalQty(item) - getDispatchedQty(item)) * 1000) / 1000, 0);

// On editable indents, items with nothing left to pick (requested − dispatched = 0) are hidden.
// Read-only indents (Ready / Dispatched / Received) keep showing every item.
const getVisibleItems = (indent) => {
    const items = indent.items || [];
    return EDITABLE_STATUSES.has(normStatus(indent.status))
        ? items.filter((item) => getRemainingQty(item) > 0)
        : items;
};

const getSortTime = (indent) => new Date(indent.updatedAt || indent.createdAt || 0).getTime() || 0;

// Latest updated first; Received indents always go to the end (still latest-first among themselves).
const sortRows = (rows) =>
    [...rows].sort((a, b) => {
        const aRank = normStatus(a.status) === "RECEIVED" ? 1 : 0;
        const bRank = normStatus(b.status) === "RECEIVED" ? 1 : 0;
        if (aRank !== bRank) return aRank - bRank;
        return getSortTime(b) - getSortTime(a);
    });

const isNewIndent = (indent) =>
    normStatus(indent.status) !== "RECEIVED" && Date.now() - getSortTime(indent) < NEW_BADGE_WINDOW_MS;

const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const mergeById = (previous, incoming) => {
    const seen = new Set(previous.map((row) => row._id));
    return [...previous, ...incoming.filter((row) => !seen.has(row._id))];
};

const extractRows = (response) => {
    const data = response?.data?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

/* ─── outlet details (state, city, address, coordinates) ──── */
const toNumber = (value) => (hasValue(value) ? Number(value) : NaN);
const validCoords = (lat, lng) =>
    Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);

// Tolerates {latitude,longitude}, {lat,lng}, {coordinates:{…}}, {location:{…}} and GeoJSON [lng, lat].
const getOutletCoords = (outlet = {}) => {
    const pairs = [
        [outlet.latitude, outlet.longitude],
        [outlet.lat, outlet.lng ?? outlet.lon ?? outlet.long],
        [outlet.coordinates?.latitude, outlet.coordinates?.longitude],
        [outlet.coordinates?.lat, outlet.coordinates?.lng],
        [outlet.location?.latitude, outlet.location?.longitude],
        [outlet.location?.lat, outlet.location?.lng],
    ];
    for (const [a, b] of pairs) {
        const lat = toNumber(a);
        const lng = toNumber(b);
        if (validCoords(lat, lng)) return { lat, lng };
    }
    const geo = outlet.location?.coordinates || outlet.coordinates;
    if (Array.isArray(geo) && geo.length >= 2) {
        const lng = toNumber(geo[0]);
        const lat = toNumber(geo[1]);
        if (validCoords(lat, lng)) return { lat, lng };
    }
    return null;
};

// "Landscape pinto park , " -> "Landscape pinto park"
const getOutletAddress = (outlet = {}) =>
    String(outlet.address || "").replace(/\s+/g, " ").replace(/[\s,]+$/, "").trim();

const getDirectionsUrl = (outlet = {}) => {
    const coords = getOutletCoords(outlet);
    const destination = coords
        ? `${coords.lat},${coords.lng}`
        : [outlet.name, getOutletAddress(outlet), outlet.city, outlet.state].filter(Boolean).join(", ");
    if (!destination) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
};

const getOutletPlace = (outlet = {}) => [outlet.city, outlet.state].filter(Boolean).join(", ");

/* ─── draft persistence (a reload must not wipe typed quantities) ─── */
const loadDrafts = () => {
    try {
        return JSON.parse(window.sessionStorage.getItem(DRAFT_STORAGE_KEY)) || {};
    } catch (e) {
        return {};
    }
};
const saveDrafts = (drafts) => {
    try {
        window.sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts));
    } catch (e) {
        /* storage unavailable — drafts just won't survive a reload */
    }
};

/* ─── per-indent summary + outcome ────────────────────────── */
// "Full" means the picked qty equals the NEW requested qty (requested − already dispatched).
// Only visible items are counted (items with nothing left to pick are hidden).
const summarize = (indent, draft = {}) => {
    const items = getVisibleItems(indent);
    let entered = 0;
    let positive = 0;
    let full = 0;
    items.forEach((item) => {
        const raw = draft[getRmId(item)];
        if (!hasValue(raw)) return;
        entered += 1;
        const quantity = Number(raw);
        if (quantity > 0) positive += 1;
        if (quantity === getRemainingQty(item)) full += 1;
    });
    const total = items.length;
    return { total, entered, positive, full, short: total - full, allFull: total > 0 && full === total };
};

/* ─── tiny UI pieces ──────────────────────────────────────── */
const Chevron = ({ open }) => (
    <svg
        width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B6B7C" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"
        className={`shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
    >
        <path d="m6 9 6 6 6-6" />
    </svg>
);

const Spinner = ({ className = "border-white/40 border-t-white" }) => (
    <span className={`w-4 h-4 rounded-full border-2 animate-spin ${className}`} />
);

const BalanceChip = ({ balance }) => {
    const number = Number(balance);
    if (Number.isNaN(number)) return null;
    let label = "Complete";
    let style = { color: "#0A6B58", background: "#E5F7F3" };
    if (number > 0) {
        label = `Short ${formatQuantity(number)}`;
        style = { color: "#B26A00", background: "#FFF7E6" };
    } else if (number < 0) {
        label = `Excess ${formatQuantity(Math.abs(number))}`;
        style = { color: "#04698F", background: "#E9F4F9" };
    }
    return (
        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap" style={style}>
            {label}
        </span>
    );
};

/* ─── Outlet filter (bottom sheet) ────────────────────────── */
const OutletSheet = ({ open, onClose, options, totalCount, selectedId, onSelect }) => {
    const [query, setQuery] = useState("");
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useEffect(() => {
        if (!open) return undefined;
        setQuery("");
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (event) => {
            if (event.key === "Escape") closeRef.current();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!open) return null;

    const q = query.trim().toLowerCase();
    const list = q
        ? options.filter((option) =>
            [option.outlet.name, option.outlet.city, option.outlet.state, option.outlet.address]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(q)
        )
        : options;

    const rowClass = (active) =>
        `w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[#F1FAFD] transition ${active ? "bg-[#F1FAFD]" : ""}`;

    return (
        <div className="fixed inset-0 z-[120] flex items-end justify-center" role="dialog" aria-modal="true" aria-label="Filter by outlet">
            <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-[#08213D]/45" />

            {/* stopPropagation keeps the page's pull-to-refresh from reacting to sheet scrolling */}
            <div
                onTouchStart={(event) => event.stopPropagation()}
                onTouchMove={(event) => event.stopPropagation()}
                className="relative w-full max-w-[430px] md:max-w-[680px] lg:max-w-[860px] max-h-[78vh] flex flex-col rounded-t-[28px] bg-white shadow-[0_-20px_50px_rgba(4,33,63,0.25)]"
            >
                <div className="px-5 pt-3 pb-3 border-b border-[#DCEAF1]">
                    <div className="w-10 h-1 rounded-full bg-[#DCEAF1] mx-auto mb-3" />
                    <div className="flex items-center justify-between">
                        <h3 className="xl-display text-[17px] font-bold text-[#08213D]">Filter by outlet</h3>
                        <button type="button" onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full bg-[#F1FAFD] text-[#5B6B7C] flex items-center justify-center">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                        </button>
                    </div>

                    {options.length > 6 && (
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Search outlet or city"
                            className="xl-body mt-3 w-full h-11 rounded-[14px] bg-[#F9FCFD] border border-[#DCEAF1] px-4 text-sm font-medium text-[#08213D] placeholder:text-[#9FB4BD] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30"
                        />
                    )}
                </div>

                <div className="overflow-y-auto pb-6">
                    {!q && (
                        <button type="button" onClick={() => onSelect(null)} className={rowClass(!selectedId)}>
                            <span className="min-w-0 flex-1">
                                <span className="xl-body block text-[14px] font-bold text-[#08213D]">All outlets</span>
                            </span>
                            <span className="xl-mono rounded-full bg-[#F1FAFD] border border-[#DCEAF1] px-2.5 py-0.5 text-[10px] font-medium text-[#08213D]">{totalCount}</span>
                            {!selectedId && <span className="text-[#00A4E3] font-bold">✓</span>}
                        </button>
                    )}

                    {list.map((option) => {
                        const active = selectedId === option.id;
                        const place = getOutletPlace(option.outlet);
                        return (
                            <button key={option.id} type="button" onClick={() => onSelect(option.id)} className={`${rowClass(active)} border-t border-[#F1FAFD]`}>
                                <span className="min-w-0 flex-1">
                                    <span className="xl-body block text-[14px] font-bold text-[#08213D] truncate">{option.outlet.name || "Unknown outlet"}</span>
                                    {place && <span className="xl-body block text-[11px] font-medium text-[#5B6B7C] truncate mt-0.5">{place}</span>}
                                </span>
                                <span className="xl-mono rounded-full bg-[#F1FAFD] border border-[#DCEAF1] px-2.5 py-0.5 text-[10px] font-medium text-[#08213D]">{option.count}</span>
                                {active && <span className="text-[#00A4E3] font-bold">✓</span>}
                            </button>
                        );
                    })}

                    {list.length === 0 && (
                        <p className="xl-body text-center text-sm font-medium text-[#5B6B7C] py-8">No outlet matches &quot;{query}&quot;</p>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── One item row ────────────────────────────────────────── */
const ItemRow = ({ item, editable, locked, value, overLimit, onChange, onFull, isLast }) => {
    const rm = item.rawMaterial || {};
    const unit = formatUnit(rm.unit);
    const originalRequested = getOriginalQty(item);
    const dispatchedSoFar = getDispatchedQty(item);
    // Editable rows show the NEW requested qty (requested − already dispatched).
    const requested = editable ? getRemainingQty(item) : originalRequested;
    const showReduction = editable && dispatchedSoFar > 0;
    const categories = [rm.mainCategoryName, rm.subCategoryName].filter(Boolean).join(" • ");
    const entered = hasValue(value);
    const quantity = Number(value);
    const isFull = entered && quantity === requested;
    const shortBy = entered && quantity < requested ? requested - quantity : 0;

    const handleKeyDown = (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        const card = event.currentTarget.closest("[data-indent-card]");
        const inputs = card ? Array.from(card.querySelectorAll("[data-qty-input]:not(:disabled)")) : [];
        const next = inputs[inputs.indexOf(event.currentTarget) + 1];
        if (next) next.focus();
        else event.currentTarget.blur();
    };

    const picked = getPickedQty(item);
    const received = hasValue(item.totalReceivedQuantity);

    return (
        <div className={`py-3 ${isLast ? "" : "border-b border-[#F1FAFD]"}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="xl-body text-[13px] font-semibold text-[#08213D] leading-snug break-words">{(rm.name || "—").trim()}</p>
                    {categories && <p className="xl-body text-[10px] font-medium text-[#5B6B7C] mt-0.5">{categories}</p>}
                </div>
                <div className="shrink-0 text-right">
                    <p className="xl-mono text-[8px] font-medium uppercase tracking-wide text-[#5B6B7C]">Requested</p>
                    <p className="xl-display text-[15px] font-bold text-[#08213D] whitespace-nowrap leading-tight">
                        {formatQuantity(requested)}
                        <span className="xl-body text-[11px] font-semibold text-[#5B6B7C] ml-1">{unit}</span>
                    </p>
                    {showReduction && (
                        <p className="xl-body text-[10px] font-medium text-[#5B6B7C] mt-0.5 whitespace-nowrap">
                            {formatQuantity(originalRequested)} − {formatQuantity(dispatchedSoFar)} dispatched
                        </p>
                    )}
                </div>
            </div>

            {editable && hasValue(picked) && (
                <p className="xl-body text-[11px] font-semibold text-[#5B6B7C] mt-1.5">
                    Already picked {formatQuantity(picked)} {unit}
                </p>
            )}

            {editable ? (
                <>
                    <div className="flex items-center gap-2 mt-2.5">
                        <div className="relative flex-1">
                            <input
                                data-qty-input
                                type="text"
                                inputMode="decimal"
                                enterKeyHint="next"
                                autoComplete="off"
                                value={value}
                                disabled={locked}
                                onChange={(event) => onChange(event.target.value)}
                                onFocus={(event) => event.target.select()}
                                onKeyDown={handleKeyDown}
                                placeholder="Qty picked"
                                aria-label={`Quantity picked for ${rm.name || "item"}`}
                                className={`xl-display w-full h-12 rounded-[14px] border pl-4 pr-16 text-[17px] font-bold focus:outline-none focus:ring-2 disabled:opacity-60 ${overLimit
                                    ? "border-red-300 bg-red-50 text-red-600 focus:ring-red-200"
                                    : isFull
                                        ? "border-[#B9E9DF] bg-[#F1FAF8] text-[#0A6B58] focus:ring-[#00A88C]/30"
                                        : entered
                                            ? "border-[#FCE4B4] bg-[#FFFBF2] text-[#08213D] focus:ring-[#E08A3C]/30"
                                            : "border-[#DCEAF1] bg-[#F9FCFD] text-[#08213D] focus:ring-[#00A4E3]/30"
                                    }`}
                            />
                            <span className="xl-body absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#5B6B7C]">{unit}</span>
                        </div>

                        <button
                            type="button"
                            onClick={onFull}
                            disabled={locked}
                            className={`xl-body h-12 px-3.5 rounded-[14px] text-[12px] font-bold border transition active:scale-95 disabled:opacity-60 ${isFull
                                ? "bg-[#00A88C] border-[#00A88C] text-white"
                                : "bg-white border-[#DCEAF1] text-[#00A4E3]"
                                }`}
                        >
                            {isFull ? "✓ Full" : "Full"}
                        </button>
                    </div>

                    {(overLimit || isFull || shortBy > 0) && (
                        <p className={`xl-body text-[10px] font-semibold mt-1.5 ${overLimit ? "text-red-500" : isFull ? "text-[#00A88C]" : "text-amber-600"}`}>
                            {overLimit
                                ? `Can't be more than requested (${formatQuantity(requested)} ${unit})`
                                : isFull
                                    ? "Matches requested qty"
                                    : `Short by ${formatQuantity(shortBy)} ${unit}`}
                        </p>
                    )}
                </>
            ) : (
                (hasValue(picked) || received) && (
                    <div className="flex items-center justify-between gap-3 mt-1.5">
                        <p className="xl-body text-[11px] font-semibold text-[#5B6B7C]">
                            {hasValue(picked) && <>Picked {formatQuantity(picked)} {unit}</>}
                            {hasValue(picked) && received && " · "}
                            {received && <>Received {formatQuantity(item.totalReceivedQuantity)} {unit}</>}
                        </p>
                        {received && hasValue(item.balanceQuantity) && <BalanceChip balance={item.balanceQuantity} />}
                    </div>
                )
            )}
        </div>
    );
};

/* ─── One indent card ─────────────────────────────────────── */
const IndentCard = ({
    indent, isOpen, onToggle, draft, onQtyChange, onFillAll, onClear, overKey,
    submitting, retryStatus, cardError, onSubmit,
}) => {
    const status = normStatus(indent.status);
    const meta = getStatusMeta(status);
    const items = getVisibleItems(indent); // zero-remaining items are hidden on editable indents
    const editable = EDITABLE_STATUSES.has(status);
    const locked = submitting || !!retryStatus; // quantities already saved → don't allow edits
    const summary = summarize(indent, draft);
    const hasDraft = summary.entered > 0;
    const willBeReady = retryStatus ? retryStatus === STATUS_READY : summary.allFull;
    const canSubmit = !!retryStatus || summary.positive > 0;
    const isNew = isNewIndent(indent);

    return (
        <div data-indent-card className="rounded-[22px] bg-white border border-[#DCEAF1]">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-3 px-4 py-4 active:opacity-70 transition"
            >
                <div className="min-w-0 text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="xl-mono text-[13px] font-bold text-[#00A4E3]">{indent.indentNumber}</span>
                        <span
                            className="xl-body rounded-full border px-2.5 py-0.5 text-[10px] font-bold"
                            style={{ color: meta.fg, background: meta.bg, borderColor: meta.border }}
                        >
                            {meta.label}
                        </span>
                        {isNew && (
                            <span className="xl-body rounded-full bg-[#00A4E3] px-2 py-0.5 text-[10px] font-bold text-white">
                                New
                            </span>
                        )}
                        {editable && hasDraft && (
                            <span className="xl-body rounded-full bg-[#E9F4F9] px-2 py-0.5 text-[10px] font-bold text-[#04698F]">
                                {summary.entered}/{summary.total} filled
                            </span>
                        )}
                    </div>
                    <p className="xl-body text-xs font-medium text-[#5B6B7C] mt-1 truncate">
                        {items.length} item{items.length === 1 ? "" : "s"} · {formatDate(indent.createdAt)}
                    </p>
                </div>
                <Chevron open={isOpen} />
            </button>

            {isOpen && (
                <div className="border-t border-dashed border-[#DCEAF1]">
                    <div className="px-4">
                        {editable && items.length > 0 && (
                            <div className="flex items-center justify-between pt-3">
                                <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-[#5B6B7C]">Enter what you picked</p>
                                <div className="flex items-center gap-3">
                                    <button type="button" onClick={onFillAll} disabled={locked} className="xl-body text-[11px] font-bold text-[#00A4E3] active:opacity-60 disabled:opacity-40">
                                        Fill all as requested
                                    </button>
                                    {hasDraft && !locked && (
                                        <button type="button" onClick={onClear} className="xl-body text-[11px] font-bold text-[#5B6B7C] active:opacity-60">
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {items.length === 0 ? (
                            <p className="xl-body text-xs font-medium text-[#5B6B7C] py-3">No items on this indent</p>
                        ) : (
                            items.map((item, idx) => {
                                const rmId = getRmId(item);
                                return (
                                    <ItemRow
                                        key={item._id || rmId || idx}
                                        item={item}
                                        editable={editable}
                                        locked={locked}
                                        value={draft?.[rmId] ?? ""}
                                        overLimit={overKey === `${indent._id}:${rmId}`}
                                        onChange={(value) => onQtyChange(item, value)}
                                        onFull={() => {
                                            const remaining = getRemainingQty(item);
                                            const isFull = hasValue(draft?.[rmId]) && Number(draft[rmId]) === remaining;
                                            onQtyChange(item, isFull ? "" : String(remaining));
                                        }}
                                        isLast={idx === items.length - 1}
                                    />
                                );
                            })
                        )}
                    </div>

                    {editable && items.length > 0 && (
                        <div className="px-3 pb-3 pt-2">
                            <div className="rounded-[18px] bg-[#F9FCFD] border border-[#DCEAF1] p-3">
                                {cardError && (
                                    <p className="xl-body text-[11px] font-semibold text-red-500 mb-2 leading-snug">{cardError}</p>
                                )}

                                {canSubmit && (
                                    <p className="xl-body text-[11px] font-semibold mb-2" style={{ color: willBeReady ? "#0A6B58" : "#B26A00" }}>
                                        {retryStatus
                                            ? "Quantities are saved. Only the status update is left."
                                            : willBeReady
                                                ? "All items complete — this indent will be marked Ready"
                                                : `${summary.short} item${summary.short === 1 ? " is" : "s are"} short or not entered — this indent will be marked Partially Ready`}
                                    </p>
                                )}

                                <button
                                    type="button"
                                    onClick={onSubmit}
                                    disabled={!canSubmit || submitting}
                                    className={`xl-display w-full h-12 rounded-[14px] text-sm font-bold flex items-center justify-center gap-2 transition active:scale-[0.98] ${canSubmit && !submitting ? "text-white" : "bg-[#E9F4F9] text-[#9FB4BD] cursor-not-allowed"}`}
                                    style={
                                        canSubmit && !submitting
                                            ? {
                                                background: willBeReady
                                                    ? "linear-gradient(135deg, #00A88C 0%, #04806D 100%)"
                                                    : "linear-gradient(135deg, #E08A3C 0%, #C26A12 100%)",
                                                boxShadow: "0 10px 24px rgba(4,33,63,0.22)",
                                            }
                                            : undefined
                                    }
                                >
                                    {submitting ? (
                                        <><Spinner /> Updating…</>
                                    ) : !canSubmit ? (
                                        "Enter quantities to update"
                                    ) : retryStatus ? (
                                        "Retry status update"
                                    ) : willBeReady ? (
                                        "Mark as Ready"
                                    ) : (
                                        "Mark as Partially Ready"
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════════════════════ */
export default function OutletWiseList({ onNotify }) {
    const storageLocation = useSelector(selectStorageLocation);
    const storageLocationId = storageLocation?._id;
    const notify = useCallback((type, message) => { if (onNotify) onNotify(type, message); }, [onNotify]);

    const [indents, setIndents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const [pagination, setPagination] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [moreError, setMoreError] = useState("");
    const [outletId, setOutletId] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [collapsedOutlets, setCollapsedOutlets] = useState(() => new Set());
    const [openIndents, setOpenIndents] = useState(() => new Set());

    // Typed quantities: { [indentId]: { [rawMaterialId]: "12.5" } }
    const [drafts, setDrafts] = useState(loadDrafts);
    const [overKey, setOverKey] = useState(null);
    const overTimerRef = useRef(null);

    const [submittingId, setSubmittingId] = useState(null);
    const [retryStatus, setRetryStatus] = useState({}); // { [indentId]: "READY" | "PARTIALLY-READY" } — quantities saved, status pending
    const [cardErrors, setCardErrors] = useState({});

    const requestRef = useRef(0);

    useEffect(() => () => { if (overTimerRef.current) window.clearTimeout(overTimerRef.current); }, []);
    useEffect(() => { saveDrafts(drafts); }, [drafts]);

    /* ─── load ─── */
    // Sorting (latest updated first, Received at the end) lives in the module-level sortRows.
    const fetchIndents = useCallback(async (mode = "initial", page = 1) => {
        const requestId = ++requestRef.current;
        try {
            if (mode === "initial") setLoading(true);
            if (mode === "refresh") setRefreshing(true);
            if (mode === "more") setLoadingMore(true);
            if (mode !== "silent") { setError(""); setMoreError(""); }

            const response = await axiosInstance.get(API.LIST, { params: { page, limit: PAGE_SIZE } });
            if (requestId !== requestRef.current) return;

            const incoming = extractRows(response)
                // Belt and braces: only this staff member's storage location.
                .filter((row) => !storageLocationId || !row.storageLocation?._id || row.storageLocation._id === storageLocationId)
                .filter((row) => !HIDDEN_STATUSES.has(normStatus(row.status)));

            setPagination(response?.data?.pagination || null);
            setIndents((previous) => sortRows(mode === "more" ? mergeById(previous, incoming) : incoming));

            // Drop saved drafts for indents the server now says are no longer pickable.
            const settled = new Set(incoming.filter((row) => !EDITABLE_STATUSES.has(normStatus(row.status))).map((row) => row._id));
            setDrafts((previous) => {
                let changed = false;
                const next = {};
                Object.keys(previous).forEach((id) => {
                    if (settled.has(id)) changed = true;
                    else next[id] = previous[id];
                });
                return changed ? next : previous;
            });
        } catch (err) {
            if (requestId !== requestRef.current) return;
            console.error("Indent list API error:", err?.response || err);
            const message = getErrorMessage(err, "Unable to load indents. Please try again.");
            if (mode === "more") {
                setMoreError(message); // keep what's already on screen
            } else if (mode !== "silent") {
                setError(message);
                setIndents([]);
                setPagination(null);
            }
        } finally {
            if (requestId === requestRef.current) {
                setLoading(false);
                setRefreshing(false);
                setLoadingMore(false);
            }
        }
    }, [storageLocationId]);

    useEffect(() => { fetchIndents("initial"); }, [fetchIndents]);

    const currentPage = pagination?.page || 1;
    const hasMore = !!pagination && (pagination.hasNextPage ?? currentPage < (pagination.totalPages || 1));

    /* ─── derived lists ─── */
    const searched = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) return indents;
        return indents.filter((indent) => {
            const haystack = [
                indent.indentNumber,
                indent?.outlet?.name,
                indent?.outlet?.city,
                ...(indent.items || []).map((item) => item?.rawMaterial?.name),
            ].filter(Boolean).join(" ").toLowerCase();
            return haystack.includes(query);
        });
    }, [indents, searchText]);

    // Outlet options come from the indents currently loaded, so every option has something to show.
    // The filter sheet stays alphabetical — easier to scan.
    const outletOptions = useMemo(() => {
        const map = new Map();
        searched.forEach((indent) => {
            const outlet = indent.outlet || {};
            const id = outlet._id || "unknown";
            if (!map.has(id)) map.set(id, { id, outlet, count: 0 });
            map.get(id).count += 1;
        });
        return [...map.values()].sort((a, b) => (a.outlet.name || "").localeCompare(b.outlet.name || ""));
    }, [searched]);

    const filtered = useMemo(
        () => (outletId ? searched.filter((indent) => (indent.outlet?._id || "unknown") === outletId) : searched),
        [searched, outletId]
    );

    // Outlet groups follow the sorted indent order: the outlet with the latest indent comes first.
    const groups = useMemo(() => {
        const map = new Map();
        filtered.forEach((indent) => {
            const outlet = indent.outlet || {};
            const id = outlet._id || "unknown";
            if (!map.has(id)) map.set(id, { id, outlet, indents: [] });
            map.get(id).indents.push(indent);
        });
        return [...map.values()];
    }, [filtered]);

    const selectedOutlet = outletId ? outletOptions.find((option) => option.id === outletId)?.outlet || null : null;

    // A single indent on screen? Open it so the user can start typing straight away.
    const soloIndentId = filtered.length === 1 ? filtered[0]._id : null;
    useEffect(() => {
        if (soloIndentId) setOpenIndents((previous) => (previous.has(soloIndentId) ? previous : new Set(previous).add(soloIndentId)));
    }, [soloIndentId]);

    const toggle = (setter, id) =>
        setter((previous) => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    /* ─── quantity entry ─── */
    const setQuantity = (indent, item, value) => {
        const rmId = getRmId(item);
        const key = `${indent._id}:${rmId}`;
        if (!isValidQuantityInput(value)) return;

        // Hard cap at the NEW requested quantity (requested − already dispatched) — flag it inline instead of a noisy toast.
        if (value !== "" && Number(value) > getRemainingQty(item)) {
            setOverKey(key);
            if (overTimerRef.current) window.clearTimeout(overTimerRef.current);
            overTimerRef.current = window.setTimeout(() => setOverKey(null), 2200);
            return;
        }

        setOverKey(null);
        setCardErrors((previous) => (previous[indent._id] ? { ...previous, [indent._id]: "" } : previous));
        setDrafts((previous) => {
            const forIndent = { ...(previous[indent._id] || {}) };
            if (value === "") delete forIndent[rmId];
            else forIndent[rmId] = value;
            const next = { ...previous };
            if (Object.keys(forIndent).length === 0) delete next[indent._id];
            else next[indent._id] = forIndent;
            return next;
        });
    };

    const fillAll = (indent) => {
        const forIndent = {};
        getVisibleItems(indent).forEach((item) => { forIndent[getRmId(item)] = String(getRemainingQty(item)); });
        setDrafts((previous) => ({ ...previous, [indent._id]: forIndent }));
    };

    const clearDraft = (indent) =>
        setDrafts((previous) => {
            const next = { ...previous };
            delete next[indent._id];
            return next;
        });

    /* ─── submit: save quantities, then set Ready / Partially Ready ─── */
    const handleSubmit = async (indent) => {
        if (submittingId) return;
        const draft = drafts[indent._id] || {};
        const summary = summarize(indent, draft);
        const pendingStatus = retryStatus[indent._id];
        if (!pendingStatus && summary.positive === 0) return;

        const targetStatus = pendingStatus || (summary.allFull ? STATUS_READY : STATUS_PARTIAL_READY);
        const setCardError = (message) => setCardErrors((previous) => ({ ...previous, [indent._id]: message }));

        setSubmittingId(indent._id);
        setCardError("");

        // Step 1 — quantities (skipped when only the status step is being retried)
        if (!pendingStatus) {
            try {
                await axiosInstance.put(API.UPDATE_QUANTITIES, {
                    storageLocation: indent.storageLocation?._id || storageLocationId,
                    items: getVisibleItems(indent)
                        .filter((item) => hasValue(draft[getRmId(item)]))
                        .map((item) => ({
                            indent: indent._id,
                            rawMaterial: getRmId(item),
                            // Still the original requested qty from the server — only the screen shows requested − dispatched.
                            requestedQuantity: getOriginalQty(item),
                            quantity: Number(draft[getRmId(item)]),
                        })),
                });
            } catch (err) {
                console.error("Update quantities error:", err?.response || err);
                const message = getErrorMessage(err, "Couldn't save the quantities. Nothing was changed — please try again.");
                setCardError(message);
                notify("error", message);
                setSubmittingId(null);
                return;
            }
        }

        // Step 2 — status
        // The API rejects "already PARTIALLY-READY" (and "already READY"), so when the indent is
        // already at the target status the quantities saved in step 1 are all that is needed.
        const alreadyAtTarget = normStatus(indent.status) === normStatus(targetStatus);
        if (!alreadyAtTarget) {
            try {
                await axiosInstance.put(API.UPDATE_STATUS, { status: targetStatus }, { params: { _id: indent._id } });
            } catch (err) {
                console.error("Update status error:", err?.response || err);
                setRetryStatus((previous) => ({ ...previous, [indent._id]: targetStatus }));
                const message = `Quantities saved, but the status wasn't updated. ${getErrorMessage(err, "Please retry.")}`;
                setCardError(message);
                notify("error", message);
                setSubmittingId(null);
                return;
            }
        }

        // Done — update the screen immediately (bump updatedAt so it jumps to the top),
        // then quietly re-sync with the server.
        setIndents((previous) =>
            sortRows(
                previous.map((row) =>
                    row._id === indent._id ? { ...row, status: targetStatus, updatedAt: new Date().toISOString() } : row
                )
            )
        );
        clearDraft(indent);
        setRetryStatus((previous) => { const next = { ...previous }; delete next[indent._id]; return next; });
        setCardErrors((previous) => { const next = { ...previous }; delete next[indent._id]; return next; });
        setOpenIndents((previous) => { const next = new Set(previous); next.delete(indent._id); return next; });
        notify("success", `${indent.indentNumber} marked ${targetStatus === STATUS_READY ? "Ready" : "Partially Ready"}`);
        setSubmittingId(null);
        fetchIndents("silent");
    };

    /* ─── render ─── */
    const emptyTitle = "Nothing to pick right now";
    const toPickCount = filtered.filter((indent) => EDITABLE_STATUSES.has(normStatus(indent.status))).length;

    return (
        <div>
            {/* Search */}
            <div className="relative mb-3">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00A4E3]">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
                    </svg>
                </span>
                <input
                    type="search"
                    inputMode="search"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Search indent no., outlet, or item"
                    className="xl-body w-full h-12 rounded-[16px] bg-white border border-[#DCEAF1] pl-11 pr-10 text-sm font-medium text-[#08213D] placeholder:text-[#9FB4BD] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30"
                />
                {searchText && (
                    <button type="button" onClick={() => setSearchText("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#F1FAFD] text-[#5B6B7C] flex items-center justify-center">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                    </button>
                )}
            </div>

            {/* Outlet filter + refresh */}
            {!loading && !error && (
                <div className="flex items-center gap-2 mb-4">
                    <button
                        type="button"
                        onClick={() => setSheetOpen(true)}
                        className={`xl-body flex-1 min-w-0 h-11 rounded-[14px] border px-3.5 flex items-center gap-2.5 text-left active:scale-[0.99] transition ${outletId ? "bg-[#E9F4F9] border-[#00A4E3]/40" : "bg-white border-[#DCEAF1]"}`}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#04698F" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                            <path d="M3 9.5 4.5 4h15L21 9.5M3 9.5a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M5 12.5V20h14v-7.5" />
                        </svg>
                        <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-[#08213D]">
                            {outletId ? selectedOutlet?.name || "Outlet" : "All outlets"}
                        </span>
                        <span className="xl-mono shrink-0 text-[10px] font-medium text-[#5B6B7C]">{outletOptions.length}</span>
                        <Chevron open />
                    </button>

                    {outletId && (
                        <button type="button" onClick={() => setOutletId(null)} className="xl-body h-11 px-3.5 rounded-[14px] bg-white border border-[#DCEAF1] text-[11px] font-bold text-[#5B6B7C] active:scale-95 transition">
                            Clear
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => fetchIndents("refresh")}
                        disabled={refreshing || loading}
                        aria-label="Refresh list"
                        className="w-11 h-11 shrink-0 rounded-[14px] bg-white border border-[#DCEAF1] flex items-center justify-center text-[#00A4E3] active:scale-95 transition disabled:opacity-50"
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={refreshing ? "animate-spin" : ""}>
                            <path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Body */}
            {loading ? (
                <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-8 text-center">
                    <div className="w-10 h-10 mx-auto rounded-full border-[3px] border-[#00A4E3]/20 border-t-[#00A4E3] animate-spin" />
                    <p className="xl-body mt-4 text-sm font-semibold text-[#08213D]">Loading indents</p>
                </div>
            ) : error ? (
                <div className="rounded-[24px] bg-red-50 border border-red-100 p-6 text-center">
                    <p className="xl-body text-sm font-semibold text-red-500">{error}</p>
                    <button type="button" onClick={() => fetchIndents("initial")} className="xl-body mt-4 px-6 h-11 rounded-[14px] bg-red-500 text-white text-xs font-semibold active:scale-95 transition">
                        Try again
                    </button>
                </div>
            ) : groups.length === 0 ? (
                <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-8 text-center">
                    <p className="xl-body text-sm font-semibold text-[#08213D]">
                        {searchText ? `No indents match "${searchText}"` : outletId ? "No indents for this outlet" : emptyTitle}
                    </p>
                    {!searchText && !outletId && (
                        <p className="xl-body text-xs font-medium text-[#5B6B7C] mt-1">New requests will show up here automatically</p>
                    )}
                    {(searchText || outletId) && (
                        <button
                            type="button"
                            onClick={() => { setSearchText(""); setOutletId(null); }}
                            className="xl-body mt-4 px-6 h-11 rounded-[14px] text-white text-xs font-semibold active:scale-95 transition"
                            style={{ background: "#00A4E3" }}
                        >
                            {outletId ? "Show all outlets" : "Clear search"}
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-5">
                    <p className="xl-body text-xs font-semibold text-[#5B6B7C]">
                        {filtered.length} indent{filtered.length === 1 ? "" : "s"} · {groups.length} outlet{groups.length === 1 ? "" : "s"}
                        {toPickCount > 0 && ` · ${toPickCount} to pick`}
                    </p>

                    {groups.map((group) => {
                        const { outlet } = group;
                        const collapsed = collapsedOutlets.has(group.id);
                        const place = getOutletPlace(outlet);
                        const directionsUrl = getDirectionsUrl(outlet);
                        const address = getOutletAddress(outlet);

                        return (
                            <section key={group.id}>
                                <div className="flex items-start gap-2 pb-2.5">
                                    <button
                                        type="button"
                                        onClick={() => toggle(setCollapsedOutlets, group.id)}
                                        aria-expanded={!collapsed}
                                        className="flex-1 min-w-0 flex items-start gap-3 px-1 text-left active:opacity-70 transition"
                                    >
                                        <span className="w-10 h-10 shrink-0 rounded-[14px] bg-[#E9F4F9] text-[#04698F] flex items-center justify-center">
                                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 9.5 4.5 4h15L21 9.5M3 9.5a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M5 12.5V20h14v-7.5" />
                                            </svg>
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="xl-display block text-[16px] font-bold text-[#08213D] leading-tight truncate">{outlet.name || "Unknown outlet"}</span>
                                            <span className="xl-body block text-[11px] font-medium text-[#5B6B7C] mt-0.5 truncate">
                                                {place ? `${place} · ` : ""}{group.indents.length} indent{group.indents.length === 1 ? "" : "s"}
                                            </span>
                                            {/* {address && (
                                                <span className="xl-body block text-[11px] font-medium text-[#9FB4BD] mt-0.5 line-clamp-2">{address}</span>
                                            )} */}
                                        </span>
                                        <span className="pt-1"><Chevron open={!collapsed} /></span>
                                    </button>

                                    {directionsUrl && (
                                        <a
                                            href={directionsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Directions to ${outlet.name || "outlet"}`}
                                            className="xl-body shrink-0 h-9 px-3 rounded-full bg-white border border-[#DCEAF1] text-[11px] font-bold text-[#04698F] flex items-center gap-1.5 active:scale-95 transition"
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="m3 11 18-8-8 18-2-8-8-2Z" />
                                            </svg>
                                            Directions
                                        </a>
                                    )}
                                </div>

                                {!collapsed && (
                                    <div className="space-y-3">
                                        {group.indents.map((indent) => (
                                            <IndentCard
                                                key={indent._id}
                                                indent={indent}
                                                isOpen={openIndents.has(indent._id)}
                                                onToggle={() => toggle(setOpenIndents, indent._id)}
                                                draft={drafts[indent._id]}
                                                onQtyChange={(item, value) => setQuantity(indent, item, value)}
                                                onFillAll={() => fillAll(indent)}
                                                onClear={() => clearDraft(indent)}
                                                overKey={overKey}
                                                submitting={submittingId === indent._id}
                                                retryStatus={retryStatus[indent._id]}
                                                cardError={cardErrors[indent._id]}
                                                onSubmit={() => handleSubmit(indent)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>
                        );
                    })}

                    {(hasMore || moreError) && (
                        <div className="text-center">
                            {moreError && <p className="xl-body text-xs font-semibold text-red-500 mb-2">{moreError}</p>}
                            <button
                                type="button"
                                onClick={() => fetchIndents("more", currentPage + 1)}
                                disabled={loadingMore}
                                className="xl-body h-11 px-6 rounded-[14px] bg-white border border-[#DCEAF1] text-xs font-bold text-[#04698F] active:scale-95 transition disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {loadingMore && <Spinner className="border-[#04698F]/25 border-t-[#04698F]" />}
                                {loadingMore ? "Loading…" : moreError ? "Try again" : "Load more"}
                            </button>
                        </div>
                    )}
                </div>
            )}

            <OutletSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                options={outletOptions}
                totalCount={searched.length}
                selectedId={outletId}
                onSelect={(id) => { setOutletId(id); setSheetOpen(false); }}
            />
        </div>
    );
}